import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KENDO_WINDOW } from '@progress/kendo-angular-dialog';
import { Customer, CustomerContactInfo } from '@raptor-fe/core';
import { ApplicationContextStore, CustomerApiService, TranslationPipe } from '@raptor-fe/services';
import { ConversationWidgetComponent } from './conversation-widget/conversation-widget.component';
import { CustomerTextWindowService } from './customer-text-window.service';

/**
 * Mounts the single, app-scoped conversation window (Spec #76394 Frontends part). Placed once in the
 * omni-web app shell so the window floats above ANY screen the launcher is triggered from -- the page
 * behind stays interactive. Renders the three states owned by `CustomerTextWindowService`: open
 * (non-modal draggable Kendo window), minimized (a pill docked bottom-right), and closed (nothing).
 * The window's titlebar is the drag handle and shows a move cursor via the `.k-window-titlebar` rule
 * in the styles.
 *
 * Ported from `omni-web-mockups/customer-messaging/messaging-window-host.component.{ts,scss}`. The
 * template and styles are copied verbatim (Tier 1); the mockup's static `contacts.json` lookup for the
 * titlebar name and the widget's consent state is replaced by resolving the customer's primary contact
 * from `CustomerApiService.get` (Tier 3), and the fixed titlebar/tooltip strings are localized through
 * the shared `TranslationService` in line with the F2 conversation-widget port.
 */
@Component({
    selector: 'lib-messaging-window-host',
    imports: [KENDO_WINDOW, ConversationWidgetComponent, TranslationPipe],
    template: `
        @if (windowService.isOpen()) {
            <kendo-window
                [width]="520"
                [height]="580"
                [minWidth]="360"
                [minHeight]="380"
                [top]="90"
                [draggable]="true"
                [resizable]="true"
                (close)="windowService.close()"
                [class.window-flash]="flash()"
            >
                <kendo-window-titlebar>
                    <span class="k-window-title">
                        <i class="bi bi-chat-dots me-2"></i>{{ { feature: 'Customer Messaging', component: 'Messaging Window', label: 'Messaging' } | translate }} &mdash; {{ activeName() }}
                    </span>
                    <button
                        type="button"
                        class="k-button k-window-titlebar-action minimize-action"
                        [title]="{ feature: 'Customer Messaging', component: 'Messaging Window', label: 'Minimize' } | translate"
                        [attr.aria-label]="{ feature: 'Customer Messaging', component: 'Messaging Window', label: 'Minimize' } | translate"
                        (click)="windowService.minimize()"
                    >
                        <i class="bi bi-dash-lg"></i>
                    </button>
                    <button
                        kendoWindowMaximizeAction
                        [attr.aria-label]="{ feature: 'Customer Messaging', component: 'Messaging Window', label: 'Maximize' } | translate"
                    ></button>
                    <button
                        kendoWindowCloseAction
                        [attr.aria-label]="{ feature: 'Customer Messaging', component: 'Messaging Window', label: 'Close' } | translate"
                    ></button>
                </kendo-window-titlebar>

                <lib-conversation-widget
                    [customerId]="windowService.context()!.customerId"
                    [contact]="contact()"
                    [originEntityId]="windowService.context()!.originEntityId"
                    [originEntityType]="windowService.context()!.originEntityType"
                />
            </kendo-window>
        }

        @if (windowService.isMinimized()) {
            <button
                type="button"
                class="messaging-dock-pill btn btn-primary rounded-pill shadow"
                (click)="windowService.restore()"
                [title]="{ feature: 'Customer Messaging', component: 'Messaging Window', label: 'Reopen conversation' } | translate"
            >
                <i class="bi bi-chat-dots me-2"></i>
                <span class="dock-name">{{ activeName() }}</span>
                <i class="bi bi-chevron-up ms-2"></i>
            </button>
        }
    `,
    styleUrls: ['./messaging-window-host.component.scss'],
})
export class MessagingWindowHostComponent {
    protected readonly windowService = inject(CustomerTextWindowService);
    private readonly customerApi = inject(CustomerApiService);
    private readonly appContext = inject(ApplicationContextStore);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly flash = signal(false);

    /**
     * The active conversation's customer identity + consent state, resolved from the window context's
     * `customerId`. Feeds the titlebar/dock name and is passed straight through to the widget so the
     * composer gates on the same record the launcher gated on.
     */
    protected readonly contact = signal<CustomerContactInfo | null>(null);

    protected activeName(): string {
        return this.contact()?.name ?? '';
    }

    constructor() {
        effect(() => {
            const context = this.windowService.context();
            if (!context) {
                untracked(() => this.contact.set(null));
                return;
            }
            const customerId = context.customerId;
            untracked(() => this.resolveContact(customerId));
        });

        effect(() => {
            this.windowService.focusPing();
            if (this.windowService.isOpen()) {
                this.flash.set(true);
                setTimeout(() => this.flash.set(false), 700);
            }
        });
    }

    /** Resolves the customer's primary-contact consent/phone into the read-only `CustomerContactInfo` the widget needs. */
    private resolveContact(customerId: string): void {
        this.customerApi
            .get(this.appContext.tenantId(), customerId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((customer) => this.contact.set(toContactInfo(customer)));
    }
}

/** Derives the widget's read-only contact view from a loaded customer's primary contact. */
function toContactInfo(customer: Customer | null): CustomerContactInfo | null {
    if (!customer || !customer.id) {
        return null;
    }
    const primary = customer.primaryContact;
    return {
        customerId: customer.id,
        name: primary?.name ?? customer.name,
        phoneNumber: primary?.phoneNumber ?? null,
        isTextApproved: primary?.isTextApproved ?? false,
    };
}
